import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

const namespace = 'http://www.w3.org/2000/svg';
const configurations = new Map();
let instance = 0;

function svgElement(tag, attributes = {}) {
  const element = document.createElementNS(namespace, tag);
  for (const [key, value] of Object.entries(attributes)) element.setAttribute(key, String(value));
  return element;
}

// Rectify photographed artwork as one source plane before the crown projection.
// The source photo is left intact, including its grain and engraving strokes.
function photoPlane(defs, sourceId, quad, box, prefix) {
  const [x, y, width, height] = box;
  const [p, q, , r] = quad;
  const sx=q[0]-p[0], sy=q[1]-p[1], tx=r[0]-p[0], ty=r[1]-p[1], det=sx*ty-tx*sy;
  const m00=width*ty/det, m01=-width*tx/det, m10=-height*sy/det, m11=height*sx/det;
  const level = svgElement('g', { id: `${prefix}-level`, 'data-photo-rectification': 'true', transform: `matrix(${m00} ${m10} ${m01} ${m11} ${x-m00*p[0]-m01*p[1]} ${y-m10*p[0]-m11*p[1]})` });
  level.append(svgElement('use', { href: `#${sourceId}` }));
  return level;
}

async function configuration(url) {
  if (!configurations.has(url)) {
    const promise = fetch(url).then(response => {
      if (!response.ok) throw new Error('Preview configuration unavailable');
      return response.json();
    }).catch(error => { configurations.delete(url); throw error; });
    configurations.set(url, promise);
  }
  return configurations.get(url);
}

export function previewPlacement(category, team) {
  if (category === 'bottle') return { x: 395, y: 460, width: 210, skew: 0 };
  if (category === 'tumbler') return { x: 340, y: 440, width: 310, skew: 0 };
  // Front-facing Richardson 112 blanks share a centered seam and square frame.
  // The photographed crown/bill junction is approximately y680.
  return { centerX: 500, centerY: 480, unitsPerInch: 112, skew: 0 };
}

export function supportsBlank(config, url, team) {
  try {
    const parsed = new URL(url, location.href);
    if (!['https:', 'http:'].includes(parsed.protocol)) return false;
    const storefrontFile = parsed.origin === location.origin && parsed.pathname.startsWith('/cdn/shop/files/');
    if (parsed.hostname !== 'cdn.shopify.com' && !storefrontFile) return false;
    return team || config.catalog_blanks.includes(parsed.pathname.split('/').pop());
  } catch { return false; }
}

export function teamHatArtworkKey(shape, artwork) {
  const keys = {
    Contour: ['hat-contour', 'hat-contour-wordmark'],
    'Rounded Square': ['hat-rectangle', 'hat-square-wordmark'],
    Oval: ['hat-oval', 'hat-oval-wordmark'],
    Rectangle: ['hat-rectangular-wc', 'hat-wordmark'],
    Circle: ['hat-circle-wc', 'hat-circle-wordmark'],
    Hexagon: ['hat-hexagon-wc', 'hat-hexagon-wordmark'],
  };
  return keys[shape]?.[artwork === 'Wellington Colts' ? 1 : 0];
}

export function personalizedHatArtworkKey(shape) {
  const keys = { Rectangle: 'personalized-patch-rectangle', 'Rounded Square': 'personalized-patch-rounded-square', Circle: 'personalized-patch-circle', Oval: 'personalized-patch-oval', Hexagon: 'personalized-patch-hexagon', 'Custom Contour': 'personalized-patch-contour' };
  return keys[shape];
}

class EvermarkdProductPreview extends Component {
  #controller;
  #observer;
  #visibility;
  #waitingForVisibility = false;
  #scope;
  #media;
  #version = 0;
  #uploadURL;
  #layout;
  #file;
  #original = false;
  #nativeColor;
  #nativeGeneration = 0;
  #id = `em-preview-svg-${++instance}`;

  connectedCallback() { super.connectedCallback(); this.#bind(); }
  updatedCallback() { super.updatedCallback(); this.#bind(); }
  disconnectedCallback() {
    this.#nativeGeneration++;
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#visibility?.disconnect();
    this.#version++;
    this.removeAttribute('aria-busy');
    if (this.#uploadURL) URL.revokeObjectURL(this.#uploadURL);
    this.#uploadURL = undefined;
    this.#file = undefined;
    this.#restore();
    super.disconnectedCallback();
  }

  #bind() {
    this.#version++;
    this.#nativeGeneration++;
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#visibility?.disconnect();
    this.#waitingForVisibility = false;
    this.#controller = new AbortController();
    this.#scope = this.dataset.catalog === 'true' ? this.closest('.card-gallery') : this.dataset.cartColor ? this.closest('.cart-items__table-row') : this.dataset.team === 'true' ? this.closest('article') : this.closest('.shopify-section, dialog');
    if (!this.#scope) return;
    if (this.dataset.cartColor) {
      this.#layout = null;
      try {
        const saved = JSON.parse(this.dataset.cartLayout || 'null');
        const numbers = saved && [saved.fit.x, saved.fit.y, saved.fit.width, saved.fit.height, saved.area.width, saved.area.height, ...saved.original, ...Object.values(saved.bounds)];
        if (numbers?.every(Number.isFinite) && saved.area.width > 0 && saved.area.height > 0 && saved.bounds.width > 0 && saved.bounds.height > 0) this.#layout = {
          normalized: { x: saved.fit.x / saved.area.width, y: saved.fit.y / saved.area.height, width: saved.fit.width / saved.area.width, height: saved.fit.height / saved.area.height },
          original: saved.original, bounds: saved.bounds
        };
      } catch { this.#layout = null; }
      // Cart morphs may retain this element while replacing its rendered children.
      // Rehydrate only untouched server markup; loading/failure status prevents loops.
      this.#observer = new MutationObserver(() => {
        if (!this.querySelector('[data-preview-canvas] svg') && !this.querySelector('[data-preview-status]')?.textContent.trim()) this.#update();
      });
      this.#observer.observe(this, { childList: true, subtree: true });
      this.#update();
      return;
    }
    const options = { signal: this.#controller.signal };
    this.#scope.addEventListener('change', this.#change, options);
    if (this.dataset.personalizedPatch === 'true') this.#scope.addEventListener('evermarkd:artwork-layout', this.#artworkLayout, options);
    this.#scope.addEventListener('evermarkd:team-selection', this.#teamSelection, options);
    if (this.dataset.teamProduct === 'true') this.#scope.addEventListener(StandardEvents.productSelect, this.#nativeVariant, options);
    this.querySelector('[data-preview-toggle]')?.addEventListener('click', this.#toggle, options);
    this.#observer = new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes].some(node => node instanceof Element && (node.matches('.gpo-app, input[data-field-name="Hat Color"]') || node.querySelector('.gpo-app, input[data-field-name="Hat Color"]'))))) this.#update();
    });
    this.#observer.observe(this.#scope, { childList: true, subtree: true });
    if ((this.dataset.team === 'true' || this.dataset.catalog === 'true') && typeof IntersectionObserver !== 'undefined') {
      this.#waitingForVisibility = true;
      if (this.dataset.catalog === 'true') {
        // Reserve the final frame without showing an unrelated product color
        // while below-fold composites wait for visibility and image decoding.
        this.hidden = false;
        const gallery = this.#gallery();
        if (gallery) gallery.hidden = true;
        const canvas = this.querySelector('[data-preview-canvas]');
        if (canvas) canvas.hidden = false;
        const status = this.querySelector('[data-preview-status]');
        if (status && !canvas?.querySelector('svg')) status.textContent = `${this.dataset.loading}: ${this.dataset.defaultColor}`;
      }
      const visibilityVersion = this.#version;
      this.#visibility = new IntersectionObserver(entries => {
        if (visibilityVersion !== this.#version || !this.isConnected || !this.#waitingForVisibility) return;
        if (entries.some(entry => entry.isIntersecting)) { this.#waitingForVisibility = false; this.#visibility.disconnect(); this.#update(); }
      }, { rootMargin: '250px 0px' });
      this.#visibility.observe(this.#scope);
    } else this.#update();
  }

  #choice() {
    if (this.dataset.personalizedPatch === 'true') {
      const selected = this.dataset.cartColor ? this.dataset.patchShape : this.#scope.querySelector('[data-personalized-patch-shape]:checked')?.value;
      this.dataset.patchShape = selected || this.dataset.patchShape || 'Rectangle';
      this.dataset.artwork = personalizedHatArtworkKey(this.dataset.patchShape) || 'personalized-patch-rectangle';
      const contour = this.dataset.patchShape === 'Custom Contour';
      const border = this.dataset.cartColor ? this.dataset.patchBorder : this.#scope.querySelector('[data-personalized-patch-border]:checked')?.value;
      this.dataset.patchBorder = !contour && border === 'Black border' ? 'Black border' : 'No border';
    }
    if (this.dataset.cartColor) return { color: this.dataset.cartColor, canonical: true };
    if (this.dataset.teamProduct === 'true') {
      const teamColor = this.dataset.team === 'true' ? this.#scope.querySelector('.em-team-colors__radio:checked')?.value : undefined;
      return { color: teamColor || this.#nativeColor || this.dataset.defaultColor, nativeTeam: true };
    }
    if (this.dataset.catalog === 'true') return { color: this.dataset.defaultColor, canonical: true };
    if (this.dataset.team === 'true') {
      const shape = this.#scope.querySelector('[data-team-shape]')?.value;
      const artwork = this.#scope.querySelector('[data-team-artwork]')?.value;
      const key = teamHatArtworkKey(shape, artwork);
      if (key) this.dataset.artwork = key;
      const input = this.#scope.querySelector('.em-team-colors__radio:checked');
      return input ? { color: input.dataset.previewColor || input.value, url: input.dataset.previewImage } : this.dataset.defaultColor ? { color: this.dataset.defaultColor, canonical: true } : undefined;
    }
    const input = this.#scope.querySelector('.gpo-app input[data-field-name="Hat Color"]:checked');
    if (!input) return this.dataset.defaultColor ? { color: this.dataset.defaultColor, canonical: true } : undefined;
    if (input.value.split('/').length > 2) return undefined;
    return { color: input.value, canonical: true };
  }

  #teamSelection = event => {
    const key = teamHatArtworkKey(event.detail?.shape, event.detail?.artwork);
    if (!key) return;
    this.dataset.artwork = key;
    this.#original = false;
    this.#update();
  };

  #nativeVariant = async event => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;
    const generation = ++this.#nativeGeneration;
    try {
      const { detail } = await event.promise;
      if (generation !== this.#nativeGeneration || !this.isConnected) return;
      const color = detail?.resource?.option1 ?? detail?.resource?.options?.[0];
      if (!color) return;
      this.#nativeColor = color;
      const shape = detail.resource.option2 ?? detail.resource.options?.[1];
      const artwork = detail.resource.option3 ?? detail.resource.options?.[2] ?? this.dataset.patchArtwork;
      const key = teamHatArtworkKey(shape, artwork);
      if (key) this.dataset.artwork = key;
      this.#original = false;
      this.#update();
    } catch { /* Keep the last completed image if variant resolution fails. */ }
  };

  #change = event => {
    if (this.#scope.querySelector('[data-team-shape]')) return;
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.matches('.em-team-colors__radio, .gpo-app input[data-field-name="Hat Color"], [data-personalized-patch-shape], [data-personalized-patch-border], input[type="file"][name="properties[Artwork or photo]"]')) {
      this.#original = false;
      this.#waitingForVisibility = false;
      this.#visibility?.disconnect();
      this.#update();
    }
  };

  #gallery() {
    if (this.dataset.catalog === 'true') {
      const gallery = this.parentElement?.querySelector(':scope > a.contents');
      if (gallery) this.#media = gallery;
      return gallery || this.#media;
    }
    if (this.dataset.team === 'true' || this.dataset.cartColor) {
      const image = this.parentElement?.querySelector(':scope > img');
      if (image) this.#media = image;
      return image || this.#media;
    }
    let sibling = this.nextElementSibling;
    while (sibling && !sibling.matches('media-gallery')) sibling = sibling.nextElementSibling;
    if (sibling) this.#media = sibling;
    return sibling || this.#media;
  }

  #restore() { const gallery = this.#gallery(); if (gallery) gallery.hidden = false; }

  #toggle = () => {
    this.#original = !this.#original;
    this.#applyVisibility();
  };

  #applyVisibility() {
    const gallery = this.#gallery();
    if (gallery) gallery.hidden = !this.#original;
    const canvas = this.querySelector('[data-preview-canvas]');
    if (canvas) canvas.hidden = this.#original;
    const toggle = this.querySelector('[data-preview-toggle]');
    if (toggle) { toggle.textContent = this.#original ? this.dataset.return : this.dataset.original; toggle.setAttribute('aria-expanded', String(this.#original)); }
  }

  #artworkLayout = event => {
    this.#layout = event.detail;
    this.#original = false;
    this.#update();
  };

  #upload() {
    if (!this.dataset.cartColor && this.#scope.querySelector('evermarkd-artwork-layout')) return this.#layout?.url;
    if (this.dataset.cartColor) {
      const url = this.dataset.cartUpload;
      try {
        const parsed = new URL(url);
        return parsed.protocol === 'https:' && parsed.hostname === 'cdn.shopify.com' && /\.(png|jpe?g)$/i.test(parsed.pathname) ? url : undefined;
      } catch { return undefined; }
    }
    const file = this.#scope.querySelector('input[type="file"][name="properties[Artwork or photo]"]')?.files?.[0];
    if (file === this.#file) return this.#uploadURL;
    if (this.#uploadURL) URL.revokeObjectURL(this.#uploadURL);
    this.#file = file;
    this.#uploadURL = file && file.size <= 10 * 1024 * 1024 && /\.(png|jpe?g)$/i.test(file.name) ? URL.createObjectURL(file) : undefined;
    return this.#uploadURL;
  }

  async #update() {
    if (this.#waitingForVisibility) return;
    const version = ++this.#version;
    const choice = this.#choice();
    if (!choice?.url && !choice?.canonical && !choice?.nativeTeam) { this.removeAttribute('aria-busy'); this.hidden = true; this.#restore(); return; }
    // Keep the last completed preview in place until its replacement is ready.
    // Restoring the original gallery here causes a wrong-color image flash.
    const canvas = this.querySelector('[data-preview-canvas]');
    if (canvas?.querySelector('svg')) this.#applyVisibility();
    else if (this.dataset.catalog !== 'true') {
      if (this.dataset.personalizedPatch === 'true' || this.dataset.teamProduct === 'true' || this.#scope.querySelector('[data-team-shape]')) {
        const gallery = this.#gallery();
        if (gallery) gallery.hidden = true;
      } else this.#restore();
    }
    this.setAttribute('aria-busy', 'true');
    const loadingStatus = this.querySelector('[data-preview-status]');
    if (loadingStatus) loadingStatus.textContent = `${this.dataset.loading}: ${choice.color}`;
    const loadingToggle = this.querySelector('[data-preview-toggle]');
    if (loadingToggle) loadingToggle.hidden = true;
    this.hidden = false;
    try {
      const config = await configuration(this.dataset.config);
      const artwork = config.artwork[this.dataset.artwork];
      if (this.dataset.teamProduct === 'true') {
        const colors = artwork?.category === 'hat' ? config.team_hat_colors : JSON.parse(this.dataset.teamColors || '{}');
        choice.url = colors?.[choice.color];
      } else if (choice.canonical) choice.url = config.catalog_colors?.[choice.color];
      else if (this.dataset.team === 'true' && artwork?.category === 'hat') choice.url = config.team_hat_colors?.[choice.color] || choice.url;
      if (!artwork || !supportsBlank(config, choice.url, this.dataset.team === 'true' || this.dataset.teamProduct === 'true')) throw new Error('Unsupported preview');
      // Only background blanks are resized. Artwork boxes remain in their
      // original source coordinates, including the rectified WC photograph.
      if (this.dataset.catalog === 'true' || this.dataset.cartColor) {
        const measured = this.#scope.getBoundingClientRect().width;
        const required = (measured || (this.dataset.cartColor ? 120 : 320)) * Math.min(window.devicePixelRatio || 1, 2);
        const width = [240, 480, 720, 960, 1280].find(size => size >= required) || 1280;
        const blank = new URL(choice.url, location.href);
        blank.searchParams.set('width', String(width));
        choice.url = blank.href;
      }
      const uploaded = artwork.custom ? this.#upload() : undefined;
      const borderlessPhoto = this.dataset.personalizedPatch === 'true' && artwork.custom && artwork.photoPatch && this.dataset.patchBorder !== 'Black border';
      const inkURL = borderlessPhoto ? artwork.borderlessPhotoUrl : artwork.ink?.asset ? new URL(artwork.ink.asset, new URL(this.dataset.config, location.href)).href : artwork.url;
      if (borderlessPhoto && !inkURL) throw new Error('Missing borderless patch photograph');
      const sources = [choice.url, ...(artwork.custom ? [...(artwork.photoPatch ? [inkURL] : []), ...(uploaded ? [uploaded] : [])] : [inkURL]), ...(artwork.materialPhoto ? [artwork.materialPhoto.url] : [])];
      await Promise.all(sources.map(url => new Promise((resolve, reject) => { const image = new Image(); image.onload = resolve; image.onerror = reject; image.src = url; })));
      if (version !== this.#version || !this.isConnected) return;
      this.toggleAttribute('data-converted-shape', Boolean(artwork.patchShape));
      const svg = this.#render(artwork, choice, uploaded, inkURL);
      this.querySelector('[data-preview-canvas]')?.replaceChildren(svg);
      const status = this.querySelector('[data-preview-status]');
      if (status) status.textContent = `${this.dataset.label}: ${choice.color}${this.dataset.personalizedPatch === 'true' ? `, ${this.dataset.patchShape}` : ''}`;
      const note = this.querySelector('[data-preview-note]');
      if (note && artwork.custom) note.textContent = artwork.contourProof ? this.dataset.contourNote : uploaded ? this.dataset.customNote : this.dataset.uploadNote;
      const toggle = this.querySelector('[data-preview-toggle]');
      if (toggle) toggle.hidden = Boolean(artwork.patchShape);
      this.hidden = false;
      this.#applyVisibility();
      this.removeAttribute('aria-busy');
    } catch {
      if (version !== this.#version || !this.isConnected) return;
      if (this.dataset.personalizedPatch === 'true' || this.dataset.teamProduct === 'true' || this.#scope.querySelector('[data-team-shape]') || this.hasAttribute('data-converted-shape') || ['hat-rectangle', 'hat-wordmark'].includes(this.dataset.artwork) && this.dataset.team === 'true') {
        const gallery = this.#gallery();
        if (gallery) gallery.hidden = true;
      } else this.#restore();
      this.querySelector('[data-preview-canvas]')?.replaceChildren();
      this.querySelector('[data-preview-canvas]')?.setAttribute('hidden', '');
      const status = this.querySelector('[data-preview-status]');
      if (status) status.textContent = this.dataset.unavailable;
      const toggle = this.querySelector('[data-preview-toggle]');
      if (toggle) toggle.hidden = true;
      this.hidden = false;
      this.removeAttribute('aria-busy');
    }
  }

  #render(artwork, choice, uploaded, inkURL) {
    const svg = svgElement('svg', { viewBox: artwork.category === 'hat' ? '75 75 850 850' : '0 0 1000 1000', role: 'img', 'aria-label': `${this.dataset.title}, ${choice.color}, ${this.dataset.personalizedPatch === 'true' ? `${this.dataset.patchShape}, ` : ''}${this.dataset.label}` });
    svg.append(svgElement('image', { href: choice.url, width: 1000, height: 1000, preserveAspectRatio: 'xMidYMid meet' }));
    if (artwork.contourProof) return svg;
    const placement = previewPlacement(artwork.category, this.dataset.team === 'true');
    const [x, y, width, height] = artwork.box;
    const patch = artwork.patchSizeInches;
    if (artwork.photoPatch) {
      // The complete frontal patch already includes material, engraving and
      // perspective. One image plane avoids doubling curvature or strip seams.
      const pw = placement.unitsPerInch * patch.width;
      const ph = placement.unitsPerInch * patch.height;
      const holder = svgElement('g', { transform: `translate(${placement.centerX - pw / 2} ${placement.centerY - ph / 2})`, 'data-continuous-photograph': 'true' });
      const defs = svgElement('defs');
      const contactId = `${this.#id}-contact`;
      const contact = svgElement('filter', { id: contactId, x: '-5%', y: '-5%', width: '110%', height: '110%' });
      contact.append(svgElement('feDropShadow', { dx: 0, dy: 1.2, stdDeviation: .8, 'flood-color': '#20150e', 'flood-opacity': '.22' }));
      defs.append(contact);
      holder.append(defs);
      const plane = svgElement('svg', { width: pw, height: ph, viewBox: artwork.photoBox.join(' '), preserveAspectRatio: 'none', overflow: 'hidden', 'data-photographic-plane': 'true' });
      const photo = svgElement('image', { href: this.dataset.personalizedPatch === 'true' && artwork.custom ? inkURL : artwork.url, width: artwork.size[0], height: artwork.size[1], 'data-photographic-patch': 'true' });
      const borderless = this.dataset.personalizedPatch === 'true' && artwork.custom && this.dataset.patchBorder !== 'Black border' && artwork.photoOutlinePath;
      const surface = photo;
      if (borderless) {
        photo.setAttribute('data-patch-border', 'No border');
        photo.setAttribute('data-borderless-texture', 'true');
      } else if (this.dataset.personalizedPatch === 'true') photo.setAttribute('data-patch-border', 'Black border');
      plane.append(surface);
      const framed = svgElement('g', { filter: `url(#${contactId})` });
      if (artwork.photoOutlinePath && (artwork.custom || artwork.patchShape === 'rounded-square')) {
        const clipId = `${this.#id}-photo-outline`;
        const clip = svgElement('clipPath', { id: clipId, clipPathUnits: 'userSpaceOnUse' });
        clip.append(svgElement('path', { d: artwork.photoOutlinePath }));
        defs.append(clip);
        surface.setAttribute('clip-path', `url(#${clipId})`);
        framed.append(plane);
      } else framed.append(plane);
      holder.append(framed);
      if (artwork.custom && uploaded) {
        const filterId = `${this.#id}-upload`;
        const filter = svgElement('filter', { id: filterId, 'color-interpolation-filters': 'sRGB' });
        filter.append(svgElement('feColorMatrix', { type: 'matrix', values: '0 0 0 0 .12 0 0 0 0 .10 0 0 0 0 .08 -.2126 -.7152 -.0722 1 0' }));
        defs.append(filter);
        const layout = this.#layout?.normalized;
        const [ix, iy, iw, ih] = layout ? [layout.x, layout.y, layout.width, layout.height] : artwork.uploadInset;
        const uploadClipId = `${this.#id}-upload-outline`;
        const uploadClip = svgElement('clipPath', { id: uploadClipId, clipPathUnits: 'userSpaceOnUse' });
        const [bx, by, bw, bh] = artwork.photoBox;
        uploadClip.append(svgElement('path', { d: artwork.photoOutlinePath, transform: `matrix(${pw / bw} 0 0 ${ph / bh} ${-bx * pw / bw} ${-by * ph / bh})` }));
        defs.append(uploadClip);
        if (this.#layout?.bounds && this.#layout.original) {
          const [ow, oh] = this.#layout.original;
          const bounds = this.#layout.bounds;
          const trimmed = svgElement('svg', { x: pw * ix, y: ph * iy, width: pw * iw, height: ph * ih, viewBox: `${bounds.x * ow} ${bounds.y * oh} ${bounds.width * ow} ${bounds.height * oh}`, preserveAspectRatio: 'xMidYMid meet', overflow: 'hidden', filter: `url(#${filterId})`, 'data-customer-artwork': 'true' });
          trimmed.append(svgElement('image', { href: uploaded, width: ow, height: oh }));
          holder.append(trimmed);
        } else holder.append(svgElement('image', { href: uploaded, x: pw * ix, y: ph * iy, width: pw * iw, height: ph * ih, preserveAspectRatio: 'xMidYMid meet', filter: `url(#${filterId})`, 'data-customer-artwork': 'true', 'clip-path': `url(#${uploadClipId})` }));
      }
      svg.append(holder);
      return svg;
    }
    const targetWidth = artwork.category === 'hat'
      ? placement.unitsPerInch * (patch.width || patch.height * width / height)
      : placement.width;
    const targetHeight = artwork.category === 'hat'
      ? placement.unitsPerInch * patch.height / .96
      : targetWidth * height / width;
    const targetX = placement.centerX === undefined ? placement.x : placement.centerX - targetWidth / 2;
    const targetY = placement.centerY === undefined ? placement.y : placement.centerY - targetHeight / 2 - Math.tan(placement.skew * Math.PI / 180) * targetWidth / 2;
    const holder = svgElement('g', { transform: `translate(${targetX} ${targetY}) skewY(${placement.skew})` });
    const crop = svgElement('svg', { width: targetWidth, height: targetHeight, viewBox: `${x} ${y} ${width} ${height}`, preserveAspectRatio: artwork.category === 'hat' ? 'none' : 'xMidYMid meet', overflow: 'hidden' });
    const materialSurface = artwork.custom || Boolean(artwork.patchShape && !artwork.photoPatch);
    const shapedPoints = artwork.patchShape === 'round'
      ? Array.from({ length: 64 }, (_, index) => { const angle = index * Math.PI / 32; return [x + width / 2 + Math.cos(angle) * width / 2, y + height / 2 + Math.sin(angle) * height / 2]; })
      : artwork.patchShape === 'rounded-square'
        ? Array.from({ length: 4 }, (_, corner) => Array.from({ length: 9 }, (_, step) => {
            const radius = .075, angle = (-90 + corner * 90 + step * 90 / 8) * Math.PI / 180;
            const cx = corner === 0 || corner === 1 ? 1 - radius : radius;
            const cy = corner === 0 || corner === 3 ? radius : 1 - radius;
            return [x + width * (cx + radius * Math.cos(angle)), y + height * (cy + radius * Math.sin(angle))];
          })).flat()
        : undefined;
    const patchPoints = shapedPoints || (artwork.custom && artwork.category === 'hat'
      ? [[x + width * .035, y], [x + width * .965, y], [x + width, y + height * .045], [x + width, y + height * .955], [x + width * .965, y + height], [x + width * .035, y + height], [x, y + height * .955], [x, y + height * .045]]
      : artwork.points);
    const defs = svgElement('defs');
    const shadeId = `${this.#id}-shade`;
    if (artwork.category === 'hat') {
      const shade = svgElement('linearGradient', { id: shadeId, x1: '0%', x2: '100%' });
      shade.append(svgElement('stop', { offset: '0%', 'stop-color': '#000', 'stop-opacity': '.12' }));
      shade.append(svgElement('stop', { offset: '50%', 'stop-color': '#000', 'stop-opacity': '0' }));
      shade.append(svgElement('stop', { offset: '100%', 'stop-color': '#000', 'stop-opacity': '.12' }));
      defs.append(shade);
    }
    const clipId = `${this.#id}-clip`;
    const clip = svgElement('clipPath', { id: clipId, clipPathUnits: 'userSpaceOnUse' });
    if (patchPoints) {
      clip.append(svgElement('polygon', { points: patchPoints.map(point => point.join(',')).join(' ') }));
    } else clip.append(svgElement('rect', { x, y, width, height }));
    defs.append(clip);
    crop.append(defs);
    if (materialSurface) {
      const materialId = `${this.#id}-material`;
      // Stable source-space grain is projected with the whole patch. A live
      // turbulence filter repaints per strip and produces vertical banding.
      const material = svgElement('pattern', { id: materialId, patternUnits: 'userSpaceOnUse', width: 64, height: 64 });
      let seed = 12;
      const random = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
      if (artwork.materialPhoto) {
        const texture = artwork.materialPhoto;
        const [, , tw, th] = texture.box;
        material.setAttribute('width', tw * 8);
        material.setAttribute('height', th * 8);
        material.setAttribute('viewBox', `0 0 ${tw * 2} ${th * 2}`);
        material.setAttribute('preserveAspectRatio', 'none');
        const tileId = `${this.#id}-grain-tile`;
        const tile = svgElement('svg', { id: tileId, width: tw, height: th, viewBox: texture.box.join(' '), overflow: 'hidden' });
        tile.append(svgElement('image', { href: texture.url, width: texture.size[0], height: texture.size[1], 'data-photographic-grain': 'true' }));
        material.append(tile);
        // Mirrored neighbors share the same edge pixels, avoiding a repeating
        // light/dark join while retaining unmodified photographic leather grain.
        for (const transform of [`translate(${tw * 2} 0) scale(-1 1)`, `translate(0 ${th * 2}) scale(1 -1)`, `translate(${tw * 2} ${th * 2}) scale(-1 -1)`]) {
          material.append(svgElement('use', { href: `#${tileId}`, transform, 'data-mirrored-grain': 'true' }));
        }
      } else for (let index = 0; index < 96; index++) {
        material.append(svgElement('circle', { cx: random() * 64, cy: random() * 64, r: .4 + random() * 1.1, fill: index % 2 ? '#472b1d' : '#ebbd8b', opacity: .08 + random() * .06 }));
      }
      defs.append(material);
      const shape = svgElement('polygon', { points: patchPoints.map(point => point.join(',')).join(' '), fill: '#b87945', stroke: '#704326', 'stroke-width': 7 });
      crop.append(shape);
      crop.append(svgElement('rect', { x, y, width, height, fill: `url(#${materialId})`, 'clip-path': `url(#${clipId})` }));
      const edge = svgElement('polygon', { points: patchPoints.map(point => point.join(',')).join(' '), fill: 'none', stroke: '#643d24', 'stroke-width': 3, transform: `translate(${(x + width / 2) * .045} ${(y + height / 2) * .045}) scale(.955)` });
      crop.append(edge);
      if (artwork.patchShape) {
        const filterId = `${this.#id}-shape-ink`;
        const filter = svgElement('filter', { id: filterId, 'color-interpolation-filters': 'sRGB' });
        filter.append(svgElement('feColorMatrix', { type: 'matrix', values: artwork.ink.asset
          ? '0 0 0 0 .12 0 0 0 0 .10 0 0 0 0 .08 -.6378 -2.1456 -.2166 1 0'
          : '0 0 0 0 .12 0 0 0 0 .10 0 0 0 0 .08 -5 0 0 0 1.5' }));
        defs.append(filter);
        const [ix, iy, iw, ih] = artwork.ink.inset;
        const inkBox = artwork.ink.frontQuad ? [0, 0, artwork.ink.box[2], artwork.ink.box[3]] : artwork.ink.box;
        const ink = svgElement('svg', { x: x + width * ix, y: y + height * iy, width: width * iw, height: height * ih, viewBox: inkBox.join(' '), preserveAspectRatio: 'xMidYMid meet', overflow: 'hidden', 'data-patch-ink': 'true' });
        const inkPhoto = svgElement('image', { href: inkURL, width: artwork.ink.size[0], height: artwork.ink.size[1], filter: `url(#${filterId})` });
        if (artwork.ink.points) {
          const inkClipId = `${this.#id}-ink-clip`;
          const inkClip = svgElement('clipPath', { id: inkClipId, clipPathUnits: 'userSpaceOnUse' });
          inkClip.append(svgElement('polygon', { points: artwork.ink.points.map(point => point.join(',')).join(' ') }));
          defs.append(inkClip);
          inkPhoto.setAttribute('clip-path', `url(#${inkClipId})`);
        }
        if (artwork.ink.frontQuad) {
          const sourceId = `${this.#id}-ink-source`;
          inkPhoto.setAttribute('id', sourceId);
          defs.append(inkPhoto);
          ink.append(photoPlane(defs, sourceId, artwork.ink.frontQuad, inkBox, `${this.#id}-ink`));
        } else ink.append(inkPhoto);
        crop.append(ink);
      }
      if (uploaded) {
        const filterId = `${this.#id}-upload`;
        const filter = svgElement('filter', { id: filterId, 'color-interpolation-filters': 'sRGB' });
        filter.append(svgElement('feColorMatrix', { type: 'matrix', values: '0 0 0 0 .12 0 0 0 0 .10 0 0 0 0 .08 -.2126 -.7152 -.0722 1 0' }));
        defs.append(filter);
        crop.append(svgElement('image', { href: uploaded, x: x + width * .12, y: y + height * .16, width: width * .76, height: height * .68, preserveAspectRatio: 'xMidYMid meet', filter: `url(#${filterId})`, 'clip-path': `url(#${clipId})` }));
      }
    } else {
      const photo = svgElement('image', { href: artwork.url, width: artwork.size[0], height: artwork.size[1] });
      if (!artwork.photoPatch) photo.setAttribute('clip-path', `url(#${clipId})`);
      else photo.setAttribute('data-photographic-patch', 'true');
      if (artwork.engraving) {
        const filterId = `${this.#id}-engraving`;
        const filter = svgElement('filter', { id: filterId, 'color-interpolation-filters': 'sRGB' });
        const alpha = artwork.category === 'bottle' ? '-20 0 20 0 1' : '20 0 -20 0 1';
        filter.append(svgElement('feColorMatrix', { type: 'matrix', values: `0 0 0 0 .72 0 0 0 0 .74 0 0 0 0 .76 ${alpha}` }));
        defs.append(filter);
        photo.setAttribute('filter', `url(#${filterId})`);
      }
      crop.append(photo);
    }
    if (artwork.category === 'hat' && !artwork.photoPatch) {
      crop.append(svgElement('rect', { x, y, width, height, fill: `url(#${shadeId})`, 'clip-path': `url(#${clipId})` }));
    }
    if (artwork.category === 'hat') {
      // Project the complete patch, including lettering and its outline, onto
      // the rounded crown. Keep the midpoint fixed on the calibrated seam.
      // Shared source geometry avoids separately distorting the engraving.
      const surfaceId = `${this.#id}-surface`;
      const surface = svgElement('g', { id: surfaceId });
      for (const child of [...crop.children]) if (child !== defs) surface.append(child);
      defs.append(surface);
      let projectedSourceId = surfaceId;
      if (artwork.frontQuad && artwork.photoPatch) {
        // This reference is already front-facing. Keep its material continuous
        // instead of tessellating a near-square photograph into visible cells.
        const [sx, sy, sw, sh] = artwork.photoBox;
        const level = svgElement('g', { id: `${this.#id}-level`, 'clip-path': `url(#${clipId})`, 'data-continuous-photograph': 'true' });
        const imagePlane = svgElement('g', { transform: `matrix(${width / sw} 0 0 ${height / sh} ${x - sx * width / sw} ${y - sy * height / sh})` });
        imagePlane.append(svgElement('use', { href: `#${surfaceId}` }));
        level.append(imagePlane);
        level.append(svgElement('rect', { x, y, width, height, fill: `url(#${shadeId})` }));
        defs.append(level);
        projectedSourceId = level.id;
      } else if (artwork.frontQuad && !artwork.patchShape) {
        // Rectify the source camera plane as a mesh before wrapping it onto the
        // crown. Every original pixel, outline and engraving travels together.
        projectedSourceId = `${this.#id}-level`;
        const level = svgElement('g', { id: projectedSourceId });
        const [tl, tr, br, bl] = artwork.frontQuad;
        const sourcePoint = (u, v) => [
          (1-u)*(1-v)*tl[0]+u*(1-v)*tr[0]+u*v*br[0]+(1-u)*v*bl[0],
          (1-u)*(1-v)*tl[1]+u*(1-v)*tr[1]+u*v*br[1]+(1-u)*v*bl[1],
        ];
        let triangleIndex = 0;
        const triangle = coordinates => {
          const src = coordinates.map(([u,v]) => sourcePoint(u,v));
          const dst = coordinates.map(([u,v]) => [x+u*width,y+v*height]);
          const [p,q,r] = src, [a,b,c] = dst;
          const sx=q[0]-p[0], sy=q[1]-p[1], tx=r[0]-p[0], ty=r[1]-p[1];
          const det=sx*ty-tx*sy;
          const m00=((b[0]-a[0])*ty-(c[0]-a[0])*sy)/det;
          const m01=((c[0]-a[0])*sx-(b[0]-a[0])*tx)/det;
          const m10=((b[1]-a[1])*ty-(c[1]-a[1])*sy)/det;
          const m11=((c[1]-a[1])*sx-(b[1]-a[1])*tx)/det;
          const meshClipId = `${this.#id}-mesh-${triangleIndex++}`;
          const meshClip = svgElement('clipPath', { id: meshClipId, clipPathUnits: 'userSpaceOnUse' });
          const cx=src.reduce((sum,p)=>sum+p[0],0)/3, cy=src.reduce((sum,p)=>sum+p[1],0)/3;
          const expanded=src.map(([px,py])=>{const length=Math.hypot(px-cx,py-cy);return [px+(px-cx)/length*1.4,py+(py-cy)/length*1.4];});
          meshClip.append(svgElement('polygon', { points: expanded.map(p=>p.join(',')).join(' ') }));
          defs.append(meshClip);
          const cell = svgElement('g', { transform: `matrix(${m00} ${m10} ${m01} ${m11} ${a[0]-m00*p[0]-m01*p[1]} ${a[1]-m10*p[0]-m11*p[1]})` });
          cell.append(svgElement('use', { href: `#${surfaceId}`, 'clip-path': `url(#${meshClipId})` }));
          level.append(cell);
        };
        for (let row=0;row<4;row++) for (let column=0;column<8;column++) {
          const u=column/8, v=row/4, right=(column+1)/8, bottom=(row+1)/4;
          triangle([[u,v],[right,v],[right,bottom]]);
          triangle([[u,v],[right,bottom],[u,bottom]]);
        }
        defs.append(level);
      }
      const contactId = `${this.#id}-contact`;
      const contact = svgElement('filter', { id: contactId, x: '-5%', y: '-5%', width: '110%', height: '110%' });
      contact.append(svgElement('feDropShadow', { dx: 0, dy: height * .009, stdDeviation: height * .006, 'flood-color': '#20150e', 'flood-opacity': '.28' }));
      defs.append(contact);
      if (materialSurface) {
        // Paint blank material once. Repeated antialiased strip clips create
        // tonal bands on a flat fill at small rendered sizes.
        const warp = ([px, py]) => {
          const u = 2 * (px - x) / width - 1;
          const vertical = .96 - .035 * u * u;
          return [x + width * (1 + Math.sin(u * .5) / Math.sin(.5)) / 2,
            y + height * ((1 - vertical) / 2 + .025 * u * u) + (py - y) * vertical];
        };
        const outline = (points, inset = 1) => points.flatMap((p, index) => {
          const q = points[(index + 1) % points.length];
          return Array.from({ length: 8 }, (_, step) => warp([
            x + width / 2 + (p[0] + (q[0] - p[0]) * step / 8 - x - width / 2) * inset,
            y + height / 2 + (p[1] + (q[1] - p[1]) * step / 8 - y - height / 2) * inset,
          ]).join(','));
        }).join(' ');
        const materialClipId = `${this.#id}-continuous-clip`;
        const materialClip = svgElement('clipPath', { id: materialClipId, clipPathUnits: 'userSpaceOnUse' });
        materialClip.append(svgElement('polygon', { points: outline(patchPoints) }));
        defs.append(materialClip);
        const blank = svgElement('g', { 'data-continuous-material': 'true', filter: `url(#${contactId})` });
        blank.append(svgElement('polygon', { points: outline(patchPoints), fill: '#b87945', stroke: artwork.materialPhoto ? '#35251b' : '#704326', 'stroke-width': artwork.materialPhoto ? 11 : 7 }));
        blank.append(svgElement('rect', { x, y, width, height, fill: `url(#${this.#id}-material)`, 'clip-path': `url(#${materialClipId})` }));
        blank.append(svgElement('polygon', { points: outline(patchPoints, .955), fill: 'none', stroke: artwork.materialPhoto ? '#29231b' : '#643d24', 'stroke-width': artwork.materialPhoto ? 13 : 3 }));
        blank.append(svgElement('rect', { x, y, width, height, fill: `url(#${shadeId})`, 'clip-path': `url(#${materialClipId})` }));
        for (const child of [...surface.children]) if (child.tagName !== 'image' && !child.hasAttribute('data-patch-ink')) child.remove();
        crop.append(blank);
      }
      const projected = svgElement('g', { filter: `url(#${contactId})`, 'data-crown-projection': 'true' });
      const slices = artwork.custom && !uploaded ? 0 : 32;
      const point = u => {
        // Front view: both sides wrap symmetrically around the center seam.
        const angle = .5;
        const horizontal = Math.sin(u * angle) / Math.sin(angle);
        const vertical = .96 - .035 * u * u;
        return { x: width * (1 + horizontal) / 2, top: height * ((1 - vertical) / 2 + .025 * u * u), scale: vertical };
      };
      for (let index = 0; index < slices; index++) {
        const start = width * index / slices;
        const end = width * (index + 1) / slices;
        const left = point(2 * index / slices - 1);
        const right = point(2 * (index + 1) / slices - 1);
        const a = (right.x - left.x) / (end - start);
        const b = (right.top - left.top) / (end - start);
        const d = (left.scale + right.scale) / 2;
        const sliceId = `${this.#id}-slice-${index}`;
        const sliceClip = svgElement('clipPath', { id: sliceId, clipPathUnits: 'userSpaceOnUse' });
        // Subpixel clip boundaries must overlap in output pixels, otherwise
        // antialiasing exposes a repeated dark seam on flat patch material.
        const overlap = .65 * width / targetWidth;
        sliceClip.append(svgElement('rect', { x: Math.max(x, x + start - overlap), y, width: Math.min(x + width, x + end + overlap) - Math.max(x, x + start - overlap), height }));
        defs.append(sliceClip);
        const section = svgElement('g', { transform: `matrix(${a} ${b} 0 ${d} ${x + left.x - a * (x + start)} ${y + left.top - b * (x + start) - d * y})` });
        section.append(svgElement('use', { href: `#${projectedSourceId}`, 'clip-path': `url(#${sliceId})` }));
        projected.append(section);
      }
      crop.append(projected);
    }
    holder.append(crop);
    svg.append(holder);
    return svg;
  }
}

if (!customElements.get('evermarkd-product-preview')) customElements.define('evermarkd-product-preview', EvermarkdProductPreview);






